/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

// cSpell:ignore timedentry, timedentrycontent, ratingreview, recarea

import { SecurityHelper } from 'sarsaparilla';
import { urlWithParams } from '../utils';

// ---------------------------
// Api Root
// ---------------------------
export const API_ROOT = process.env.API || '';

// ---------------------------
// Facility / Tours
// ---------------------------

export const getFacilityUrl = (facilityId) =>
    `${API_ROOT}/timedentrycontent/facility/${facilityId}`;

export const getToursByFacilityUrl = (facilityId) =>
    `${API_ROOT}/timedentrycontent/facility/${facilityId}/tour?includeFieldSalesOnly=false`;

export const getTourByFacilityAndTourUrl = (facilityId, tourId) =>
    `${API_ROOT}/timedentrycontent/facility/${facilityId}/tour/${tourId}?includeFieldSalesOnly=false`;

// ---------------------------
// Facility availability, times
// ---------------------------
export const getFacilityAvailabilityUrl = ({
    facilityId,
    year,
    month,
    accountType = 'FIT',
}) => {
    const baseUrl = SecurityHelper.isCommercialAccount()
        ? `${API_ROOT}/timedentry/comm/availability/facility`
        : `${API_ROOT}/timedentry/availability/facility`;
    return `${baseUrl}/${facilityId}/monthlyAvailabilitySummaryView?year=${year}&month=${month}&inventoryBucket=${accountType}`;
};

// ---------------------------
// Tour availability, times
// ---------------------------
export const getTourAvailabilityUrl = ({
    facilityId,
    year,
    month,
    accountType = 'FIT',
    tourId,
}) => {
    const baseUrl = SecurityHelper.isCommercialAccount()
        ? `${API_ROOT}/timedentry/comm/availability/facility`
        : `${API_ROOT}/timedentry/availability/facility`;
    return `${baseUrl}/${facilityId}/monthlyAvailabilitySummaryView?year=${year}&month=${month}&inventoryBucket=${accountType}&tourID=${tourId}`;
};

export const getFarthestAvailabilityUrl = (facilityId) =>
    `${API_ROOT}/timedentry/availability/facility/${facilityId}/farthest`;

export const getTourTimesUrl = ({ facilityId, tourId, reservationId, date }) => {
    const tourPath = tourId ? `/tour/${tourId}` : '';
    const reservationPath = reservationId ? `&reservation_id=${reservationId}` : '';
    const baseUrl = SecurityHelper.isCommercialAccount()
        ? `${API_ROOT}/timedentry/comm/availability/facility`
        : `${API_ROOT}/timedentry/availability/facility`;
    return `${baseUrl}/${facilityId}${tourPath}?date=${date}${reservationPath}`;
};

// ---------------------------
// Pricing View
// ---------------------------
export const getPricingViewUrl = (facilityId) =>
    `${process.env.API}/timedentry/facility/${facilityId}/pricing/view`;

// ---------------------------
// Reservation creation and modification
// ---------------------------
export const getReservationUrl = (reservationId) =>
    `${process.env.API}/timedentry/reservation/${reservationId}`;

export const getModifyReservationUrl = (reservationId) =>
    `${process.env.API}/timedentry/reservation/${reservationId}/savePendingModifications`;

export const getApplyPendingModificationsUrl = (reservationId) =>
    `${process.env.API}/timedentry/reservation/${reservationId}/applyPendingModifications`;

export const getDiscardPendingModificationsUrl = (reservationId) =>
    `${process.env.API}/timedentry/reservation/${reservationId}/discardPendingModifications`;

export const getCreateReservationUrl = () => `${process.env.API}/timedentry/reservation`;

export const getReservationModificationWindowUrl = (reservationId) =>
    `${API_ROOT}/timedentry/reservation/${reservationId}/modificationWindow`;

export const getMyReservationsURL = () =>
    `${API_ROOT}/timedentry/reservations/myReservations`;

// ---------------------------
// Order Details
// ---------------------------
export const getOrderDetailsViewUrl = (reservationId) =>
    `${API_ROOT}/timedentry/orderDetails?reservationId=${reservationId}`;

export const getDraftPriceSummaryUrl = () =>
    `${API_ROOT}/timedentry/getDraftPriceSummary`;

export const getQRCodesUrl = (reservationId) =>
    `${API_ROOT}/timedentry/reservation/${reservationId}/tickets/qrCodes`;

export const getAppleWalletUrl = (ticketId) =>
    `${API_ROOT}/timedentry/applepass/${ticketId}`;

// ---------------------------
// PDF
// ---------------------------
export const getPDFUrl = (reservation) => {
    const facilityId = reservation?.order_details?.facility_id ?? '';
    return `${API_ROOT}/timedentry/facility/${facilityId}/reservations/${reservation.reservation_id}/tickets/pdf`;
};

export const getLargeGroupPDFUrl = (reservation) => {
    return `${API_ROOT}/timedentry/reservations/${reservation.reservation_id}/pdf`;
};

/** ***********************************
 * NON TIMED-ENTRY ENDPOINTS
 * ***********************************/

// ---------------------------
// Facility rating-review
// ---------------------------
export const getFacilityRatingReviewUrl = (facilityId) => {
    const locationType = 'Timed Entry';
    return `${API_ROOT}/ratingreview/aggregate?location_id=${facilityId}&location_type=${encodeURIComponent(
        locationType
    )}`;
};

// ---------------------------
// Tour rating-review
// ---------------------------
export const getTourRatingReviewUrl = (tourId) => {
    const locationType = 'Tour';
    return `${API_ROOT}/ratingreview/aggregate?location_id=${tourId}&location_type=${encodeURIComponent(
        locationType
    )}`;
};

// ---------------------------
// Rec area
// ---------------------------
export const getRecAreaUrl = (id) => {
    const args = {
        fq: ['entity_type:recarea', `entity_id:${id}`],
    };

    return urlWithParams(`${process.env.API}/search`, args);
};

// ---------------------------
// Media service
// ---------------------------
export const getTourImagesUrl = (facilityId, tourId) =>
    `/api/media/public/timedentry_tour/${facilityId}_${tourId}`;
export const getFacilityImagesUrl = (facilityId) =>
    `/api/media/public/asset/${facilityId}`;

// ---------------------------
// Gateway / Tours
// ---------------------------
export const getToursByGatewayUrl = (gatewayId) =>
    `${API_ROOT}/timedentrycontent/gateway/${gatewayId}/tour`;
